import { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, Mail, RefreshCw } from 'lucide-react';
import {
  SuperAdminService,
  type SignupCollectedEmail,
  type SignupFlowConversionReport,
} from '../../services/superAdminService';

const RANGES = [
  // days: 0 → since midnight today (loadSignupFunnelEvents subtracts then floors to 00:00)
  { days: 0, label: '1d' },
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
];

const BAR_COLORS = [
  'bg-gradient-to-r from-blue-500 to-blue-600',
  'bg-gradient-to-r from-green-500 to-green-600',
  'bg-gradient-to-r from-yellow-500 to-yellow-600',
  'bg-gradient-to-r from-orange-500 to-orange-600',
  'bg-gradient-to-r from-red-500 to-red-600',
  'bg-gradient-to-r from-blue-500 to-blue-600',
  'bg-gradient-to-r from-emerald-500 to-emerald-600',
  'bg-gradient-to-r from-violet-500 to-violet-600',
];

const FLOW_LABELS: Record<string, string> = {
  nexus: 'Signup-nexus',
  standard: 'Standard',
  extended: 'Extended',
  partner: 'Partner',
};

function FunnelReportCard({ report }: { report: SignupFlowConversionReport }) {
  const [mode, setMode] = useState<'all' | 'unique'>('all');
  const data = mode === 'unique' ? report.unique : report.all;
  const maxCount = Math.max(0, ...data.funnelData.map((s) => s.count));

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-col min-h-0">
      <div className="mb-3 flex-shrink-0 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
            {report.title}
          </span>
          <div className="mt-1.5 text-[11px] font-mono text-blue-600/90 break-all leading-snug">
            {report.url}
          </div>
        </div>
        <div className="flex-shrink-0 bg-black/[0.06] rounded-[8px] p-[2px] flex">
          {(['all', 'unique'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`px-2 py-[3px] text-[11px] font-semibold rounded-[6px] transition-all capitalize ${
                mode === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="text-left mb-3 flex-shrink-0">
        <div className="text-2xl font-bold text-gray-900">{data.value}</div>
        <div className="text-xs text-gray-500 mt-0.5">
          {data.label}
          {mode === 'unique' ? ' · unique visitors' : ''}
        </div>
      </div>

      <div className="space-y-2">
        {data.funnelData.map((step, index) => {
          const percentage = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
          return (
            <div key={step.step} className="flex items-center space-x-3">
              <div
                className="flex-1 bg-gray-100/50 backdrop-blur-sm rounded-xl h-5 relative overflow-hidden border border-white/30"
                style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
              >
                <div
                  className={`h-full rounded-xl transition-all duration-300 ${BAR_COLORS[index % BAR_COLORS.length]}`}
                  style={{ width: `${percentage}%` }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-medium text-gray-700">{step.count}</span>
                </div>
              </div>
              <div className="text-xs text-gray-600 min-w-0 flex-shrink-0 w-20 truncate">
                {step.step}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Super Admin tab: one Online Booking Volume–style conversion report
 * per signup entry path (standard, extended, partner, nexus), plus
 * every email collected at the email step.
 */
export default function SignupFlowReportsPanel() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState<SignupFlowConversionReport[]>([]);
  const [emails, setEmails] = useState<SignupCollectedEmail[]>([]);
  const [flowFilter, setFlowFilter] = useState<'all' | string>('all');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [reportData, emailData] = await Promise.all([
        SuperAdminService.getSignupFlowConversionReports(days),
        SuperAdminService.getSignupCollectedEmails(days),
      ]);
      setReports(reportData);
      setEmails(emailData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load signup flow reports');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredEmails = useMemo(() => {
    if (flowFilter === 'all') return emails;
    return emails.filter((e) => e.flow === flowFilter);
  }, [emails, flowFilter]);

  const flowOptions = useMemo(() => {
    const keys = [...new Set(emails.map((e) => e.flow))];
    return keys.sort();
  }, [emails]);

  const copyEmails = async () => {
    const text = filteredEmails.map((e) => e.email).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-bold text-gray-900">Sign up flow</h2>
          <p className="text-[12px] text-gray-500 mt-0.5">
            Conversion funnels by entry path — plus every email captured at the email step.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="bg-black/[0.06] rounded-[9px] p-[2px] flex">
            {RANGES.map((r) => (
              <button
                key={r.days}
                type="button"
                onClick={() => setDays(r.days)}
                className={`px-2.5 py-[5px] text-[12px] font-semibold rounded-[7px] transition-all ${
                  days === r.days ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="h-8 w-8 flex items-center justify-center text-gray-500 hover:text-gray-700 bg-white rounded-full border border-gray-200"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700">
          {error}
        </div>
      )}

      {loading && reports.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {reports.map((report) => (
              <FunnelReportCard key={report.key} report={report} />
            ))}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Mail className="h-4 w-4 text-gray-500 flex-shrink-0" />
                <div>
                  <h3 className="text-[14px] font-semibold text-gray-900">
                    Emails collected
                  </h3>
                  <p className="text-[11px] text-gray-500">
                    {filteredEmails.length} unique
                    {flowFilter !== 'all' ? ` · ${FLOW_LABELS[flowFilter] || flowFilter}` : ''}
                    {' · '}from the email step
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="bg-black/[0.06] rounded-[8px] p-[2px] flex flex-wrap">
                  <button
                    type="button"
                    onClick={() => setFlowFilter('all')}
                    className={`px-2 py-[3px] text-[11px] font-semibold rounded-[6px] transition-all ${
                      flowFilter === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                    }`}
                  >
                    All
                  </button>
                  {flowOptions.map((flow) => (
                    <button
                      key={flow}
                      type="button"
                      onClick={() => setFlowFilter(flow)}
                      className={`px-2 py-[3px] text-[11px] font-semibold rounded-[6px] transition-all ${
                        flowFilter === flow ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                      }`}
                    >
                      {FLOW_LABELS[flow] || flow}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => void copyEmails()}
                  disabled={filteredEmails.length === 0}
                  className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-gray-200 bg-white text-[12px] font-semibold text-gray-600 hover:text-gray-900 disabled:opacity-40"
                >
                  <Copy className="h-3.5 w-3.5" />
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>

            {filteredEmails.length === 0 ? (
              <div className="px-4 py-10 text-center text-[13px] text-gray-500">
                No emails collected in this range yet.
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="min-w-full text-left text-[13px]">
                  <thead className="sticky top-0 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2.5 font-semibold">Email</th>
                      <th className="px-4 py-2.5 font-semibold">Flow</th>
                      <th className="px-4 py-2.5 font-semibold">Source</th>
                      <th className="px-4 py-2.5 font-semibold">Collected</th>
                      <th className="px-4 py-2.5 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredEmails.map((row) => (
                      <tr key={`${row.email}:${row.sessionId}`} className="hover:bg-gray-50/80">
                        <td className="px-4 py-2.5 font-medium text-gray-900">
                          <a href={`mailto:${row.email}`} className="hover:text-blue-600">
                            {row.email}
                          </a>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {FLOW_LABELS[row.flow] || row.flow}
                        </td>
                        <td className="px-4 py-2.5 text-gray-500 capitalize">
                          {row.captureSource || '—'}
                        </td>
                        <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                          {formatWhen(row.collectedAt)}
                        </td>
                        <td className="px-4 py-2.5">
                          {row.completedSignup ? (
                            <span className="inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                              Signed up
                            </span>
                          ) : (
                            <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                              Email only
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
